import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter as rxFilter } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

/**
 * Serviço para detectar atualizações do app (PWA) e notificar o usuário.
 * Só tem efeito quando o app está em produção com service worker ativo.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
    private readonly swUpdate = inject(SwUpdate);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly startedAtMs = Date.now();
    private readonly notifyOnlyAfterMs = 15_000;
    private readonly checkEveryMs = 6 * 60 * 60 * 1000;
    private currentUrl = '/';
    private lastHandledHash: string | null = null;
    private isReloading = false;

    constructor() {
        this.checkForUpdates();
    }

    private checkForUpdates(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.currentUrl = this.router.url ?? '/';
        this.router.events.pipe(rxFilter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
            this.currentUrl = e.urlAfterRedirects || e.url || '/';
        });

        // Nova versão disponível
        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe((evt) => {
                const nextHash = evt.latestVersion?.hash ?? null;
                if (this.isReloading) {
                    return;
                }
                if (nextHash && this.lastHandledHash === nextHash) {
                    return;
                }
                this.lastHandledHash = nextHash;

                const elapsed = Date.now() - this.startedAtMs;
                const shouldNotify = elapsed >= this.notifyOnlyAfterMs && !this.isAuthPage();

                if (shouldNotify) {
                    this.toastr.info('Atualização disponível. Atualizando agora…', 'Atualização', {
                        timeOut: 3000,
                        extendedTimeOut: 1000,
                        closeButton: true,
                        tapToDismiss: true,
                    });
                }

                this.isReloading = true;
                this.swUpdate.activateUpdate().then(() => document.location.reload());
            });

        // Checagem imediata + periódica, e também ao voltar para a aba.
        this.swUpdate.checkForUpdate();
        setInterval(() => this.swUpdate.checkForUpdate(), this.checkEveryMs);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.swUpdate.checkForUpdate();
            }
        });
    }

    private isAuthPage(): boolean {
        const url = (this.currentUrl || '').toLowerCase();
        return (
            url.includes('/sign-in') ||
            url.includes('/sign-up') ||
            url.includes('/forgot-password') ||
            url.includes('/reset-password') ||
            url.includes('/confirmation-required') ||
            url.includes('/not-found-company')
        );
    }

    /** Força verificação manual de atualizações. */
    checkNow(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.checkForUpdate();
        }
    }
}
